import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { HourRange } from "../../../../components/UIToolkit/Inputs/TimeRangeListField";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import { SettingsSection } from "../../../../components/UIToolkit/molecules/SettingsSection";
import { CollapsibleSection } from "../../../../components/UIToolkit/Panels/CollapsibleSection";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import type { JobShiftHours, ShiftHoursSlot, ShiftHoursSource } from "../../../../services/backend.service";
import { type JobRowSaveControl, RecurringShiftHoursJobRow, toSlots } from "./RecurringShiftHoursJobRow";
import { editedJobNames, isShiftHoursSavable, shownSlots } from "./recurringDisplay";

/**
 * Whether the reviewed hours can be saved into Department Configuration from here.
 *
 * `perJob` departments get a checkbox per job and a save button. `perRole` departments keep shift
 * lengths per role, which a per-job save cannot write, so they get a pointer to where to enter them.
 */
export type ShiftHoursSaveToDepartment =
	| { mode: "perJob"; selected: readonly string[]; onToggle: (jobName: string, checked: boolean) => void; onSave: () => void; saving: boolean }
	| { mode: "perRole" };

type Props = {
	/** What the last run resolved for itself, with the provenance of each entry. */
	derived: JobShiftHours[];
	/** The operator's edits, keyed by job name. A job absent here is still whatever the run derived. */
	edits: Record<string, ShiftHoursSlot[]>;
	onEditsChange: (next: Record<string, ShiftHoursSlot[]>) => void;
	/** Re-runs the calculation with the edits confirmed. Disabled until something is actually edited. */
	onRecalculate: () => void;
	saveToDepartment: ShiftHoursSaveToDepartment;
	busy?: boolean;
};

/**
 * Jobs shown before the rest collapse behind a toggle.
 *
 * A real department has ten or more jobs, and each row carries its whole slot list plus a From/To
 * pair — so the panel grew taller than the viewport and pushed the actual proposal off screen. Five
 * is what fits above the fold while still showing a representative handful.
 */
const VISIBLE_JOB_COUNT = 5;

/**
 * Which jobs earn one of those five slots. Lower sorts first.
 *
 * Provenance IS the priority here: `inferred` hours are a guess read off the loaded file, and the
 * panel exists to get those checked. Configured and confirmed hours are already somebody's declared
 * answer, so they are the ones that can wait behind a toggle.
 */
const ATTENTION_ORDER: Record<ShiftHoursSource, number> = {
	inferred: 0,
	departmentDefault: 1,
	configured: 2,
	confirmed: 3,
};

/**
 * The hours every proposed shift is snapped onto, shown for approval rather than asked up front.
 *
 * The run resolves these itself — from the department's own configuration first, and only from the
 * loaded schedule as a fallback — so the operator never types them. But an `inferred` row is a guess
 * made from a file, and a guess that silently decides the start and end of every generated shift is
 * exactly the thing that has to be visible and correctable before anything is written.
 */
export const RecurringShiftHoursPanel: FC<Props> = ({ derived, edits, onEditsChange, onRecalculate, saveToDepartment, busy = false }) => {
	const { t } = useTranslation();
	const [showAll, setShowAll] = useState(false);

	const inferredCount = useMemo(() => derived.filter((entry) => entry.source === "inferred").length, [derived]);
	const editedJobs = useMemo(() => new Set(editedJobNames(derived, edits)), [derived, edits]);

	const { visible, hidden } = useMemo(() => {
		const ordered = [...derived].sort((a, b) => ATTENTION_ORDER[a.source] - ATTENTION_ORDER[b.source] || a.jobName.localeCompare(b.jobName));
		const head = ordered.slice(0, VISIBLE_JOB_COUNT);
		const tail = ordered.slice(VISIBLE_JOB_COUNT);
		// An edited job is never allowed to hide. Approval is blocked until it is recalculated, and the
		// warning naming it is useless if the row it names sits behind a collapsed toggle.
		return { visible: [...head, ...tail.filter((entry) => editedJobs.has(entry.jobName))], hidden: tail.filter((entry) => !editedJobs.has(entry.jobName)) };
	}, [derived, editedJobs]);

	const setJobSlots = (jobName: string, ranges: HourRange[]) => onEditsChange({ ...edits, [jobName]: toSlots(ranges) });
	const editedCount = editedJobs.size;
	const saving = saveToDepartment.mode === "perJob" && saveToDepartment.saving;
	const rowsBusy = busy || saving;

	// Only jobs a save can still write count — a selection left on a now-locked or now-empty row would inflate it.
	const selectedSavableCount = useMemo(() => {
		if (saveToDepartment.mode !== "perJob") return 0;
		const selected = new Set(saveToDepartment.selected);
		return derived.filter((entry) => selected.has(entry.jobName) && isShiftHoursSavable(entry, edits)).length;
	}, [derived, edits, saveToDepartment]);

	const saveControlFor = (entry: JobShiftHours): JobRowSaveControl | null =>
		saveToDepartment.mode === "perJob"
			? { selected: saveToDepartment.selected.includes(entry.jobName), onToggle: (checked) => saveToDepartment.onToggle(entry.jobName, checked) }
			: null;

	const renderRow = (entry: JobShiftHours) => (
		<RecurringShiftHoursJobRow
			key={entry.jobName}
			entry={entry}
			edited={editedJobs.has(entry.jobName)}
			slots={shownSlots(entry, edits)}
			onChange={(ranges) => setJobSlots(entry.jobName, ranges)}
			busy={rowsBusy}
			save={saveControlFor(entry)}
		/>
	);

	if (derived.length === 0) {
		return (
			<SettingsSection title="Shift hours per job" icon={<ScheduleIcon />} layout="stack" contentMaxWidth="none" dataTestId="recurring-shift-hours-section">
				<InlineHint tone="warning" dataTestId="recurring-shift-hours-empty">
					No shift hours could be resolved for any job. Proposed shifts fall back to the times actually worked, which is usually a sign the department's jobs
					are not configured yet.
				</InlineHint>
			</SettingsSection>
		);
	}

	return (
		<SettingsSection
			title={`Shift hours per job (${derived.length})`}
			icon={<ScheduleIcon />}
			description="Every proposed shift is snapped onto these times. Guessed hours are listed first — check them, correct any that are wrong, then recalculate."
			layout="stack"
			contentMaxWidth="none"
			dataTestId="recurring-shift-hours-section"
			action={
				<SystemButton
					buttonVariant="primary"
					size="small"
					disabled={editedCount === 0 || rowsBusy}
					loading={busy}
					onClick={onRecalculate}
					data-testid="recurring-shift-hours-recalculate"
				>
					{editedCount > 0 ? `Recalculate with ${editedCount} correction${editedCount === 1 ? "" : "s"}` : "Recalculate"}
				</SystemButton>
			}
		>
			{inferredCount > 0 && (
				<InlineHint tone="warning" dataTestId="recurring-shift-hours-inferred-warning">
					{inferredCount} job{inferredCount === 1 ? " has" : "s have"} no configured shift times, so the hours below were guessed from the loaded history.
					Confirm them before approving anything.
				</InlineHint>
			)}

			{saveToDepartment.mode === "perRole" && (
				<InlineHint tone="info" dataTestId="recurring-shift-hours-role-mode-hint">
					{t("onboarding_load.recurring_shift_hours_save_role_mode_hint")}
				</InlineHint>
			)}

			<Box sx={{ display: "flex", flexDirection: "column" }}>{visible.map(renderRow)}</Box>

			{hidden.length > 0 && (
				<CollapsibleSection
					title={
						showAll ? `Hide ${hidden.length} more job${hidden.length === 1 ? "" : "s"}` : `Show ${hidden.length} more job${hidden.length === 1 ? "" : "s"}`
					}
					open={showAll}
					onToggle={() => setShowAll((open) => !open)}
					dataTestId="recurring-shift-hours-show-all"
				>
					<Box sx={{ display: "flex", flexDirection: "column" }}>{hidden.map(renderRow)}</Box>
				</CollapsibleSection>
			)}

			{saveToDepartment.mode === "perJob" && (
				<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
					<SystemButton
						buttonVariant="secondary"
						size="small"
						disabled={selectedSavableCount === 0 || rowsBusy}
						loading={saving}
						onClick={saveToDepartment.onSave}
						data-testid="recurring-shift-hours-save-button"
					>
						{t("onboarding_load.recurring_shift_hours_save_button", { count: selectedSavableCount })}
					</SystemButton>
				</Box>
			)}
		</SettingsSection>
	);
};
